import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { type z } from 'zod';

import { queryKeysUnit } from '../queryKeys';

import { fetchUnits } from './fetchUnits';
import { type UnitsResponse, type UnitsSearchParams } from './types';
import { zodUnitsSearchParamsSchema } from './validations';

/**
 * Get the query options for fetching units.
 * @param options - Optional configuration for the query.
 * @param queryParameters - The search parameters for fetching the units.
 * @returns The query options.
 */
export const getUnitsQueryOptions = <TData = UnitsResponse>({
  queryParameters,
  options = {},
}: {
  options?: Omit<
    UseQueryOptions<UnitsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: UnitsSearchParams;
}) => {
  const validatedSearchParams =
    zodUnitsSearchParamsSchema.parse(queryParameters);

  return queryOptions<UnitsResponse, Error, TData>({
    async queryFn({ queryKey }) {
      const unitsSearchParams = queryKey[2] as z.output<
        typeof zodUnitsSearchParamsSchema
      >; // TODO: query keys should be structured as one object as first array element; getting queryKey[2] is brittle

      return fetchUnits(unitsSearchParams);
    },
    queryKey: queryKeysUnit.getAll(validatedSearchParams),
    staleTime: ms('1h'), // Cache more aggressively as this data won't change often.
    ...options,
  });
};

/**
 * React Query based custom hook for getting all defined UN/CEFACT units.
 * @param queryParameters - Search parameters for fetching units (language, unitType).
 *   - language: The language to fetch the units in (defaults to 'DE').
 *   - unitType: Optional array of unit types to filter by.
 * @param options - Optional React Query configuration (except 'queryKey' and 'queryFn').
 * @returns Query result containing the list of UN/CEFACT units and query state.
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/get_complete_unit_mapping_dictionary_units_dictionary_get
 */
export const useQueryUnits = <TData = UnitsResponse>(
  queryParameters: UnitsSearchParams,
  options?: Omit<
    UseQueryOptions<UnitsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery(
    getUnitsQueryOptions<TData>({
      queryParameters,
      options,
    }),
  );
};
